import React, { FC, useCallback, useMemo } from 'react';
import { DiscountTypeEnum, MembershipDto, MembershipPublishingStateEnum, priceFormatter } from '@sweatpals/shared';
import { useFormContext } from 'react-hook-form';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { map, pick } from 'lodash-es';
import { Select } from '../../inputs';
import { Colors, EventPriceToMembershipType } from '../../../constants';
import { PricesFormDto } from '../../../models/dto';
import { useCustomRegister } from '../../../hooks';
import { DollarSignIcon, MembershipIcon, InfoIcon, TicketsIcon } from '../../../icons';
import { CoreNumberInput, CoreTooltip } from '../../_core';
import Showable from '../../utils/showable';

export interface EventPriceToMembershipProps {
	tierIndex: number;
	membershipIndex: number;
	communityMemberships?: MembershipDto[] | undefined;
}

export const EventPriceToMembership: FC<EventPriceToMembershipProps> = props => {
	const { membershipIndex, tierIndex, communityMemberships } = props;

	const form = useFormContext<PricesFormDto>();
	const { setValue, watch, formState, getValues } = form;
	const { register } = useCustomRegister(form);
	const errors = useMemo(
		() => (formState.errors?.tiers?.[tierIndex]?.eventPricesToMemberships?.[membershipIndex] ?? {}),
		[formState.errors?.tiers, membershipIndex, tierIndex]
	);
	const membership = watch(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}`);

	const tierPriceAmount = watch(`tiers.${tierIndex}.priceAmount`, 0);
	const isDiscountTypeFixed = useMemo(() => membership.discountType === DiscountTypeEnum.Fixed, [membership.discountType]);

	const eventPriceToMembershipTypeReadable = useMemo(() => ({
		[EventPriceToMembershipType.Free]: 'Can join for free',
		[EventPriceToMembershipType.FullPrice]: 'Full price',
		[EventPriceToMembershipType.HalfPrice]: 'Half Price',
		[EventPriceToMembershipType.CustomPrice]: 'Custom'
	}), []);

	const communityMembershipsMap = useMemo(
		() => new Map(communityMemberships?.map(x => [x.id, pick(x, ['name', 'publishingState'])])),
		[communityMemberships]
	);
	const selectedCommunityMembership = useMemo(
		() => communityMembershipsMap.get(membership.membershipId),
		[communityMembershipsMap, membership.membershipId]
	);
	const isSelectedCommunityMembershipCanceled = useMemo(
		() => (!!selectedCommunityMembership && selectedCommunityMembership.publishingState === MembershipPublishingStateEnum.Canceled),
		[selectedCommunityMembership]
	);

	const onMembershipTypeChange = useCallback(
		(membershipIndex: number, newType: EventPriceToMembershipType) => {
			setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.type`, newType);

			switch (newType) {
				case EventPriceToMembershipType.Free:
					setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.priceAmount`, 0);
					setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.discountType`, DiscountTypeEnum.Percentage);
					break;
				case EventPriceToMembershipType.HalfPrice:
					setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.priceAmount`, 50);
					setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.discountType`, DiscountTypeEnum.Percentage);
					break;
				case EventPriceToMembershipType.FullPrice:
					setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.priceAmount`, 100);
					setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.discountType`, DiscountTypeEnum.Percentage);
					break;
				case EventPriceToMembershipType.CustomPrice:
					setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.priceAmount`, 0);
					setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.discountType`, DiscountTypeEnum.Fixed);
					break;
			}
		},
		[setValue, tierIndex]
	);

	const onChangeType = useCallback((newType: DiscountTypeEnum) => {
		setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.discountType`, newType);

		if (newType === DiscountTypeEnum.Percentage) {
			const amount = getValues(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.priceAmount`);
			setValue(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.priceAmount`, amount > 100 ? 100 : amount);
		}
	}, [getValues, tierIndex, membershipIndex, setValue]);

	return (
		<Box display={'flex'} flexDirection={'column'} gap={'10px'}>
			<Box display={'flex'} alignItems={'center'} gap={'10px'}>
				<Box width={'192px'} display={'flex'} gap={'10px'} alignItems={'center'}>
					<MembershipIcon style={{ width: '18px', height: '18px', color: Colors.white_a80 }} />
					<Box
						sx={{
							maxWidth: `calc(100% - ${isSelectedCommunityMembershipCanceled ? '56px' : '28px'})`,
							wordBreak: 'break-word'
						}}
					>
						{selectedCommunityMembership?.name}
					</Box>
					<Showable show={isSelectedCommunityMembershipCanceled}>
						<CoreTooltip
							arrow
							dark
							placement="top"
							sx={{
								'& .MuiTooltip-tooltip': {
									maxWidth: '260px',
									textAlign: 'center',
									fontFamily: 'Montserrat, sans-serif',
									fontSize: '14px',
									fontWeight: 600,
									lineHeight: '17px',
									color: Colors.white,
								}
							}}
							title="This membership was canceled but still appears here because some members are still enrolled"
						>
							<Box width="18px" height="18px">
								<InfoIcon
									style={{
										width: '100%',
										height: '100%',
										color: Colors.white_a60,
										cursor: 'pointer',
									}}
								/>
							</Box>
						</CoreTooltip>
					</Showable>
				</Box>
				<Box flex={1} display={'flex'} alignItems={'flex'} gap={'4px'}>
					<Select
						{...register(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.type`)}
						controlSx={{
							flex: 1,
							width: '194px',
						}}
						renderValue={id => (
							<Box display={'flex'} gap={'10px'}>
								<DollarSignIcon width={20} height={20} />
								<Typography variant="inherit">
									{id ? String(eventPriceToMembershipTypeReadable[id as EventPriceToMembershipType]) : 'Not selected'}
								</Typography>
							</Box>
						)}
						onChange={e => {
							onMembershipTypeChange(membershipIndex, e.target.value as EventPriceToMembershipType);
						}}
						fullWidth
					>
						{map(EventPriceToMembershipType, type => (
							<MenuItem
								key={type}
								value={type}
								sx={{
									gap: '10px',
								}}
							>
								<Typography
									sx={{
										color: Colors.white,
									}}
								>
									{eventPriceToMembershipTypeReadable[type]}
								</Typography>
							</MenuItem>
						))}
					</Select>
					{membership.type === EventPriceToMembershipType.CustomPrice ? (
						<Box flex={1} display={'flex'} alignItems={'flex'} gap={'4px'}>
							<CoreNumberInput
								{...(register(`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.priceAmount`, {
									type: 'number',
									passRef: false,
									passType: false,
									transformValue: (v?: number) => (v && isDiscountTypeFixed ? Math.round(Number(v) / 100) : v),
								}) as any)}
								onChange={value => {
									const newValue = value ?? 0;
									setValue(
										`tiers.${tierIndex}.eventPricesToMemberships.${membershipIndex}.priceAmount`,
										isDiscountTypeFixed ? Math.round(newValue * 100) : newValue
									);
								}}
								decimalScale={isDiscountTypeFixed ? 2 : 0}
								allowNegative={false}
								isAllowed={({ floatValue }) => !floatValue || isDiscountTypeFixed || (floatValue >= 0 && floatValue <= 100)}
								lang="en-US"
								placeholder="Unlimited"
								startAdornment={<TicketsIcon />}
								endAdornment={
									!isDiscountTypeFixed ? (
										<Typography
											sx={{
												maxWidth: '110px',
												fontFamily: 'Hind, sans-serif',
												fontSize: '13px',
												fontWeight: 500,
												lineHeight: '13px',
												color: Colors.white,
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												overflow: 'hidden',

											}}
										>
											{`End price is ${priceFormatter(tierPriceAmount * (membership.priceAmount / 100))}`}
										</Typography>
									) : null
								}
								controlSx={{ minWidth: '135px' }}
								sx={{
									'& .MuiInputBase-input': {
										flex: 1,
									}
								}}
								fullWidth
							/>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '3px',
									p: '2px 6px 3px 6px',
									borderRadius: '8px',
									backgroundColor: Colors.white_a10,
									mb: errors?.priceAmount ? '25px' : 'unset',
								}}
							>
								<Button
									onClick={() => onChangeType(DiscountTypeEnum.Fixed)}
									sx={{
										p: '5px 10px',
										minWidth: 'unset',
										backgroundColor: isDiscountTypeFixed ? Colors.white_a10 : Colors.transparent,
										border: `1px solid ${isDiscountTypeFixed ? Colors.white_a40 : Colors.transparent}`,
										borderRadius: '8px',
										color: Colors.white,
										minHeight: 'unset',
										maxHeight: '40px',
									}}
								>
									$
								</Button>
								<Button
									onClick={() => onChangeType(DiscountTypeEnum.Percentage)}
									sx={{
										p: '5px 10px',
										minWidth: 'unset',
										backgroundColor: !isDiscountTypeFixed ? Colors.white_a10 : Colors.transparent,
										border: `1px solid ${!isDiscountTypeFixed ? Colors.white_a40 : Colors.transparent}`,
										borderRadius: '8px',
										color: Colors.white,
										minHeight: 'unset',
										maxHeight: '40px',
									}}
								>
									%
								</Button>
							</Box>
						</Box>
					) : null}
				</Box>
			</Box>
		</Box>
	);
};
